import { Navigate, useRouter } from '@tanstack/react-router';

import { useGoBack } from '@libs/hooks/useGoBack';

import { useAuth } from '@features/auth/hooks/useAuth';
import { useClubCardQuery } from '@features/club/api/hooks';
import { ClockControlType } from '@features/shared/api/typings';
import { PlayerProfilePage } from '@features/user/pages/PlayerProfilePage/PlayerProfilePage';
import { PlayerAchievementsListPreviewWidget } from '@features/user/ui/PlayerAchievementsListPreview/PlayerAchievementsListPreviewWidget';
import { PlayerClubsAndTournamentsPreviewWidget } from '@features/user/ui/PlayerClubsAndTournaments';
import { PlayerClubsAndTournamentsFullWidget } from '@features/user/ui/PlayerClubsAndTournaments/PlayerClubsAndTournamentsFullWidget';
import { PlayerEloStatsWidget } from '@features/user/ui/PlayerEloStats';
import { PlayerEloStatsPreviewWidget } from '@features/user/ui/PlayerEloStatsPreview';
import { PlayerShieldAvatarWidget } from '@features/user/ui/PlayerShieldAvatar';
import { UserAuthMenuWidget } from '@features/user/ui/UserAuthMenu/UserAuthMenuWidget';
import { UserSettingsMenuWidget } from '@features/user/ui/UserSettingsMenu/UserSettingsMenuWidget';

import { PremiumSubscriptionWidget } from './components/PremiumSubscription/PremiumSubscriptionWidget';

type Props = {
  isEloStatsModalOpen: boolean;
  setIsEloStatsModalOpen: (isOpen: boolean) => void;
  isClubsAndTournamentsOpen: boolean;
  setIsClubsAndTournamentsOpen: (isOpen: boolean) => void;
  openFullGamesListClockControlType?: ClockControlType;
  setOpenFullGamesList?: (clockControlType?: ClockControlType) => void;
};
export function PlayerProfilePageWidget({
  isEloStatsModalOpen,
  setIsEloStatsModalOpen,
  isClubsAndTournamentsOpen,
  setIsClubsAndTournamentsOpen,
  openFullGamesListClockControlType,
  setOpenFullGamesList,
}: Props) {
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const firstClubId = user?.clubs && Object.keys(user.clubs)[0];
  const { data: club } = useClubCardQuery(firstClubId, { staleTime: Infinity });
  const themeColor = club?.themeColor;

  const onBack = useGoBack();

  if (!isAuthenticated) {
    return <Navigate to="/auth/required" search={{ back: router.latestLocation.href }} />;
  }

  return (
    <PlayerProfilePage
      shieldAvatar={<PlayerShieldAvatarWidget userId={user?.userId} />}
      eloStats={<PlayerEloStatsPreviewWidget setIsModalOpen={setIsEloStatsModalOpen} />}
      subscriptions={<PremiumSubscriptionWidget />}
      achievements={<PlayerAchievementsListPreviewWidget />}
      clubsAndTournaments={
        <PlayerClubsAndTournamentsPreviewWidget setIsModalOpen={setIsClubsAndTournamentsOpen} />
      }
      actions={
        <>
          <UserSettingsMenuWidget />
          <UserAuthMenuWidget />
        </>
      }
      themeColor={themeColor}
      onBack={onBack}
      modals={
        <>
          {isEloStatsModalOpen && (
            <PlayerEloStatsWidget
              onClose={() => setIsEloStatsModalOpen(false)}
              openFullGamesListClockControlType={openFullGamesListClockControlType}
              setOpenFullGamesList={setOpenFullGamesList}
            />
          )}
          {isClubsAndTournamentsOpen && (
            <PlayerClubsAndTournamentsFullWidget
              onClose={() => setIsClubsAndTournamentsOpen(false)}
            />
          )}
        </>
      }
      chessRussianProfileLink="https://chessrussian.ru/lk"
    />
  );
}
